import { Divider, Link, Stack, Typography } from '@mui/material';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { useWizard } from '@/context/wizard';
import { SupplierType } from '@/data/subsidy/interfaces';
import { KeyValue } from '@/packages/subsidy/pages/refund-request/components/key-value';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';
import {
  InvoiceWizardStepIds,
  type InvoiceWizardProps,
} from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/interfaces';
import { paymentTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/constants';
import {
  Months,
  PaymentReceiptsTypes,
  ReceiptType,
  ReceiptTypeLabels,
} from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/interfaces';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';
import { cpfMask } from '@/utils/masks/cpf';
import { cnpjMask } from '@/utils/masks/cnpj';

const keyWidth = 200;

export const Details = () => {
  const { resourceApproval } = useRefundRequest();
  const {
    values: { canEdit },
    actions: { navigate },
  } = useWizard<InvoiceWizardProps>();
  const refundRequest = resourceApproval?.refund_request;

  const receipts = resourceApproval?.refund_request?.receipts ?? [];
  const purchaseReceipts = receipts.find(
    (r) => !paymentTypes.includes(r.type as PaymentReceiptsTypes)
  );
  const paymentReceipts = receipts.find(
    (r) =>
      r.type === PaymentReceiptsTypes.Payment ||
      r.type === PaymentReceiptsTypes.BillingStatement
  );
  const othersReceipts = receipts.find(
    (r) => r.type === PaymentReceiptsTypes.Other
  );

  const isLegalPerson =
    refundRequest?.supplier_type === SupplierType.LegalPerson;

  const supplierDocumentLabel = isLegalPerson ? 'CNPJ' : 'CPF';
  const supplierDocumentValue = isLegalPerson
    ? cnpjMask(refundRequest?.supplier_document || '')
    : cpfMask(refundRequest?.supplier_document || '');

  return (
    <Stack gap={2}>
      <KeyValue
        label="Data do pagamento"
        value={formatDate(refundRequest?.payment_date || '')}
      />
      <Stack direction={{ md: 'row' }} gap={{ md: 2 }}>
        <Typography
          fontWeight={fontWeights.fontWeightSemibold}
          width={keyWidth}
        >
          Comprovante de pagamento
        </Typography>
        {paymentReceipts?.attachments?.map((item) => (
          <Link key={item.url} href={item.url} target="_blank" rel="noopener">
            {item.content.filename}
          </Link>
        ))}
      </Stack>
      <KeyValue
        label={
          refundRequest?.reference_month
            ? 'Valor da mensalidade'
            : 'Valor da compra'
        }
        value={formatMoney(refundRequest?.purchase_amount || '')}
      />
      {refundRequest?.reference_month && (
        <KeyValue
          label="Mês de referência"
          value={
            Object.values(Months)[
              (refundRequest?.reference_month as number) - 1
            ] || ''
          }
        />
      )}
      {othersReceipts && (
        <Stack direction={{ md: 'row' }} gap={{ md: 2 }}>
          <Typography
            fontWeight={fontWeights.fontWeightSemibold}
            width={keyWidth}
          >
            Outros comprovantes
          </Typography>
          {othersReceipts?.attachments?.map((item) => (
            <Link key={item.url} href={item.url} target="_blank" rel="noopener">
              {item.content.filename}
            </Link>
          ))}
        </Stack>
      )}
      <Divider sx={{ my: 2 }} />

      <KeyValue
        label="Nome do fornecedor"
        value={refundRequest?.supplier_name || ''}
      />
      {refundRequest?.supplier_type && (
        <KeyValue label={supplierDocumentLabel} value={supplierDocumentValue} />
      )}
      <KeyValue
        label="Valor do pagamento"
        value={formatMoney(refundRequest?.payment_amount || '')}
      />
      <KeyValue
        label="Data da compra"
        value={formatDate(refundRequest?.purchase_date || '')}
      />
      <Stack direction={{ md: 'row' }} gap={{ md: 2 }}>
        <Typography
          fontWeight={fontWeights.fontWeightSemibold}
          width={keyWidth}
        >
          Comprovante de compra
        </Typography>
        {purchaseReceipts?.attachments?.map((item) => (
          <Link key={item.url} href={item.url} target="_blank" rel="noopener">
            {item.content.filename}
          </Link>
        ))}
      </Stack>
      <KeyValue
        label="Tipo de comprovante"
        value={ReceiptTypeLabels[purchaseReceipts?.type as ReceiptType] || ''}
      />
      <Divider sx={{ my: 2 }} />
      <KeyValue
        label="CPF do comprador"
        value={cpfMask(refundRequest?.receiver_fiscal_document || '')}
      />
      {refundRequest?.banking_info?.pix_key && (
        <KeyValue
          label="Chave PIX"
          value={refundRequest?.banking_info?.pix_key || ''}
        />
      )}
      {refundRequest?.banking_info?.bank && (
        <>
          <KeyValue
            label="Nome completo"
            value={refundRequest?.banking_info?.holder_name || ''}
          />
          <KeyValue
            label="Banco"
            value={refundRequest?.banking_info?.bank || ''}
          />
          <KeyValue
            label="Agência"
            value={refundRequest?.banking_info?.agency || ''}
          />
          <KeyValue
            label="Número da conta"
            value={refundRequest?.banking_info?.account_number || ''}
          />
        </>
      )}
      <KeyValue
        label="Total a receber"
        value={formatMoney(refundRequest?.refundable_amount || '')}
      />

      {canEdit && (
        <Link onClick={() => navigate(InvoiceWizardStepIds.InvoiceForm)} mt={2}>
          Editar
        </Link>
      )}
    </Stack>
  );
};
